import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { geoMercator, geoPath } from "d3-geo";
import { fetchCities } from "../lib/api";
import { useT } from "../i18n/I18nProvider";
import polandBorder from "../data/polandBorder.geo.json";

// SVG canvas dimensions used by the projection. The outer container scales
// down via viewBox + w-full so the map stays responsive.
const VIEWBOX_W = 600;
const VIEWBOX_H = 600;
const PROJECTION_PADDING = 20;

// Per-city label offset (in projected pixels) to keep labels readable when
// markers sit close to each other (Katowice / Cracovie cluster in the south,
// Łódź wedged between Warsaw and Poznań). Numbers tuned manually.
const LABEL_OFFSET = {
  varsovie:   { dx: 0,   dy: -16 },
  cracovie:   { dx: 0,   dy: 22  },
  wroclaw:    { dx: 0,   dy: -16 },
  gdansk:     { dx: 0,   dy: -16 },
  poznan:     { dx: 0,   dy: -16 },
  lodz:       { dx: 0,   dy: -16 },
  katowice:   { dx: 0,   dy: -16 },
};

function offsetFor(slug) {
  return LABEL_OFFSET[slug] || { dx: 0, dy: -16 };
}

export default function PolandMap() {
  const { t, lp } = useT();
  const [cities, setCities] = useState([]);
  const [hovered, setHovered] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCities().then(setCities).catch(() => {});
  }, []);

  // Memoize projection + path so we only compute the country outline once.
  const { projection, pathD } = useMemo(() => {
    const proj = geoMercator().fitExtent(
      [
        [PROJECTION_PADDING, PROJECTION_PADDING],
        [VIEWBOX_W - PROJECTION_PADDING, VIEWBOX_H - PROJECTION_PADDING],
      ],
      polandBorder
    );
    const d = geoPath(proj)(polandBorder);
    return { projection: proj, pathD: d };
  }, []);

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8"
      data-testid="poland-map"
    >
      <div className="mb-5">
        <h3 className="font-display font-semibold text-xl text-slate-900">
          {t("map.title")}
        </h3>
        <p className="text-sm text-slate-500 mt-0.5">{t("map.sub")}</p>
      </div>
      <div className="relative aspect-square max-w-md mx-auto">
        <svg
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          className="w-full h-full"
          role="img"
          aria-label="Carte de la Pologne"
        >
          <path d={pathD} fill="#F1F5F9" stroke="#0056D2" strokeWidth="2" />
          {cities.map((c) => {
            if (typeof c.lat !== "number" || typeof c.lng !== "number") return null;
            const point = projection([c.lng, c.lat]);
            if (!point) return null;
            const [x, y] = point;
            const off = offsetFor(c.slug);
            return (
              <g
                key={c.slug}
                className="poland-pin"
                onClick={() => navigate(lp(`/villes/${c.slug}`))}
                onMouseEnter={() => setHovered(c)}
                onMouseLeave={() => setHovered(null)}
                data-testid={`map-pin-${c.slug}`}
              >
                <circle cx={x} cy={y} r="22" fill="transparent" stroke="none" />
                <circle
                  cx={x}
                  cy={y}
                  r="7"
                  fill="#DC143C"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  x={x + off.dx}
                  y={y + off.dy}
                  textAnchor="middle"
                  fontSize="13"
                  fontWeight="600"
                  fill="#0F172A"
                  className="pointer-events-none select-none"
                  style={{ fontFamily: "Outfit" }}
                >
                  {c.name_fr}
                </text>
              </g>
            );
          })}
        </svg>
        {hovered && (
          <div className="absolute top-2 right-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm text-xs">
            <div className="font-semibold text-slate-900">{hovered.name_fr}</div>
            <div className="text-slate-500">{hovered.region}</div>
          </div>
        )}
      </div>
    </div>
  );
}
